"use client";

import { useEffect, useRef, useState, type ComponentPropsWithoutRef, type PointerEvent } from "react";
import { cn } from "@/lib/utils";
import styles from "./drag-scroll-area.module.css";

export type DragScrollAreaProps = ComponentPropsWithoutRef<"div">;

export function DragScrollArea({ className, children, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onLostPointerCapture, onClickCapture, onScroll, ...props }: DragScrollAreaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; x: number; scrollLeft: number } | null>(null);
  const suppressClick = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [edges, setEdges] = useState({ start: false, end: false });

  function updateEdges() {
    const element = ref.current;
    if (!element) return;
    const next = { start: element.scrollLeft > 1, end: element.scrollLeft + element.clientWidth < element.scrollWidth - 1 };
    setEdges(current => current.start === next.start && current.end === next.end ? current : next);
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(element);
    for (const child of element.children) observer.observe(child);
    updateEdges();
    return () => observer.disconnect();
  }, [children]);

  return <div {...props} ref={ref} data-slot="drag-scroll-area" className={cn(styles.viewport, className)} data-scroll-start={edges.start || undefined} data-scroll-end={edges.end || undefined} data-dragging={dragging || undefined}
    onPointerDown={event => {
      suppressClick.current = false;
      onPointerDown?.(event);
      const element = event.currentTarget;
      if (event.defaultPrevented || !event.isPrimary || event.button !== 0 || element.scrollWidth <= element.clientWidth) return;
      if ((event.target as Element).closest("button, a, input, textarea, select, [contenteditable=true], [role=button]")) return;
      if (event.pointerType === "mouse") {
        event.preventDefault();
        if (element.tabIndex >= 0) element.focus({ preventScroll: true });
      }
      element.setPointerCapture(event.pointerId);
      drag.current = { pointerId: event.pointerId, x: event.clientX, scrollLeft: element.scrollLeft };
      setDragging(true);
    }}
    onPointerMove={event => {
      onPointerMove?.(event);
      if (event.defaultPrevented || drag.current?.pointerId !== event.pointerId) return;
      if (!(event.buttons & 1)) { endDrag(event); return; }
      const distance = drag.current.x - event.clientX;
      if (!suppressClick.current && Math.abs(distance) < 4) return;
      suppressClick.current = true;
      event.currentTarget.scrollLeft = drag.current.scrollLeft + distance;
    }}
    onPointerUp={event => { endDrag(event); onPointerUp?.(event); }}
    onPointerCancel={event => { endDrag(event); onPointerCancel?.(event); }}
    onLostPointerCapture={event => { endDrag(event); onLostPointerCapture?.(event); }}
    onClickCapture={event => {
      if (suppressClick.current && event.detail > 0) {
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClickCapture?.(event);
    }}
    onScroll={event => { updateEdges(); onScroll?.(event); }}>
    {children}
  </div>;
}
